/*
 * @format
 */

// Passkey endpoints on the Halo API. The WebAuthn options and responses are
// plain JSON rather than JSON:API: the web app's passkey library
// (@simplewebauthn/browser) produces and consumes that exact shape, and the
// JSON:API client would rename keys such as clientDataJSON.

import { fetchAuthSession } from 'aws-amplify/auth';

import config from '../../env';
import { GLOBAL_TIMEOUT } from '../utils/timeout';

export class PasskeyApiError extends Error {
  constructor(status, code, message) {
    super(message);
    this.name = 'PasskeyApiError';
    this.status = status;
    this.code = code;
  }
}

// The body as JSON, null when empty, or undefined when it isn't JSON: a
// gateway, CDN or captive portal can answer with an HTML page instead.
function parseBody(text) {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return undefined;
  }
}

// Sends a request to the passkey endpoints, with `idToken` as its
// Authorization header when given.
async function send(path, { method = 'GET', body, idToken } = {}) {
  // Given up on after GLOBAL_TIMEOUT, as other API requests are: the screens
  // wait behind a loading modal that can't be dismissed, so a stalled
  // connection must end in an error rather than hang.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), GLOBAL_TIMEOUT);
  let response;
  let text;
  try {
    response = await fetch(`${config.halo_api_endpoint}/halo/passkeys${path}`, {
      method,
      headers: {
        ...(idToken ? { Authorization: idToken } : {}),
        'Content-Type': 'application/json',
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
    text = await response.text();
  } catch (error) {
    if (controller.signal.aborted) {
      throw new PasskeyApiError(
        undefined,
        'TIMED_OUT',
        'Passkey request timed out',
      );
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }

  const data = parseBody(text);

  if (!response.ok) {
    const error = data?.errors?.[0];
    throw new PasskeyApiError(
      response.status,
      error?.code,
      error?.title || `Passkey request failed (${response.status})`,
    );
  }

  // A success that isn't JSON didn't come from the API (a captive portal's
  // page, say), so it says nothing about the account's passkeys. Taken as an
  // empty list, it would show the account as having none, and have the app
  // tell the phone's password manager to stop offering every one of them.
  if (data === undefined) {
    throw new PasskeyApiError(
      response.status,
      'UNREADABLE_RESPONSE',
      'Passkey response could not be read',
    );
  }

  return data;
}

// A request as the signed-in user, resolving to { data, account }: the API's
// answer, and the account it answered for (the sub of the ID token sent, as
// the API takes it from there). Refused here, sending nothing, when no one is
// signed in.
async function requestAs(path, { method = 'GET', body } = {}) {
  let token;
  try {
    token = (await fetchAuthSession()).tokens?.idToken;
  } catch (cause) {
    // Refreshing the session failed (offline, say), so nothing was sent.
    const error = new PasskeyApiError(
      undefined,
      'SESSION_UNAVAILABLE',
      'The session could not be read',
    );
    error.cause = cause;
    throw error;
  }
  const idToken = token?.toString();
  if (!idToken) {
    throw new PasskeyApiError(401, 'NOT_SIGNED_IN', 'Not signed in');
  }
  const data = await send(path, { method, body, idToken });
  return { data, account: token.payload?.sub ?? null };
}

// A request as the signed-in user, resolving to the API's answer.
const request = async (path, options) =>
  (await requestAs(path, options)).data;

export const getRegistrationOptions = () =>
  request('/registration-options', { method: 'POST' });

export const registerPasskey = (deviceName, credential) =>
  request('', { method: 'POST', body: { deviceName, credential } });

/**
 * Resolves to { passkeys: [{ id, DeviceName, CreatedAt, LastUsedAt, BackedUp }],
 * removedByPasswordResetAt, account }: the second is when a password reset
 * last removed the account's passkeys (epoch ms) and none has been added
 * since, or null otherwise; `account` is the sub of the account listed (its
 * passkeys' WebAuthn user ID), or null if the session didn't say. The API
 * reads the list consistently, so a passkey just added is in it.
 */
export async function listPasskeys() {
  const { data, account } = await requestAs('');
  // A success without the list (an empty body, `{}`, a proxy's JSON) didn't
  // come from the API either, and taken as no passkeys it would have the app
  // drop every one from the phone's password manager (see above).
  if (!Array.isArray(data?.data)) {
    throw new PasskeyApiError(
      undefined,
      'UNREADABLE_RESPONSE',
      'Passkey response could not be read',
    );
  }
  return {
    passkeys: data.data.map(item => ({
      id: item.id,
      ...item.attributes,
    })),
    removedByPasswordResetAt: data?.meta?.RemovedByPasswordResetAt ?? null,
    account,
  };
}

export const deletePasskey = credentialId =>
  request(`/${encodeURIComponent(credentialId)}`, { method: 'DELETE' });

// When a sign-in challenge expires (epoch seconds), by this device's clock,
// from the API's answer arriving at `arrivedAt` (epoch ms). Its `expiresIn`
// (seconds left) is measured from when the answer arrived, so a clock that's
// out doesn't matter; an API that sends only `expiresAt` gives it by its own
// clock; one that sends neither gives null.
function challengeExpiry(data, arrivedAt) {
  if (Number.isFinite(data.expiresIn) && data.expiresIn >= 0) {
    return arrivedAt / 1000 + data.expiresIn;
  }
  return Number.isFinite(data.expiresAt) ? data.expiresAt : null;
}

/**
 * A challenge for signing in with a passkey before the account is known, as
 * { options, expiresAt }: `options` is the WebAuthn request options, a fresh
 * single-use challenge with no account's passkeys listed, so the browser
 * offers any Halo passkey it holds; `expiresAt` is when the challenge expires
 * (epoch seconds, see challengeExpiry), or null from an API that doesn't say.
 * It's kept beside the options rather than in them, which go to the browser
 * and the phone's passkey module as they are.
 *
 * Asked for while signed out, so it goes without the ID token, deliberately
 * past the refusal every other request here makes when there is none. It
 * doesn't read the session either, so one that can't be read doesn't stop a
 * sign-in.
 */
export async function getSignInChallenge() {
  const data = await send('/sign-in-options', { method: 'POST' });
  const arrivedAt = Date.now();
  // Without a challenge there is nothing for the passkey to sign.
  if (typeof data?.options?.challenge !== 'string') {
    throw new PasskeyApiError(
      undefined,
      'UNREADABLE_RESPONSE',
      'Passkey response could not be read',
    );
  }
  return {
    options: data.options,
    expiresAt: challengeExpiry(data, arrivedAt),
  };
}

/** Just the WebAuthn request options from getSignInChallenge. */
export async function getSignInOptions() {
  return (await getSignInChallenge()).options;
}
